import { UseQueryResult } from '@tanstack/react-query';
import { Role, rolesManipulator } from 'auth';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import { FormikProvider, useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { matchPath, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import RegisterClassesSelectPaginate, {
  ClassItemOption,
} from '@/modules/common/components/RegisterClassesSelectPaginate.tsx';
import {
  ENTITY_ID_SEARCH_PARAM_KEY,
  REGISTER_ID_SEARCH_PARAM_KEY,
} from '@/modules/common/constants/search-params.ts';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import {
  useCreateBulkReportMutation,
  useCreateReportMutation,
  useGetReportList,
} from '@/modules/common/queries/reports.ts';
import {
  PENDING_BULK_REPORT_CHANGE_EVENT,
  getPendingBulkReport,
  setPendingBulkReport,
} from '@/modules/common/utils/pending-bulk-report.ts';
import { preventFormDefault } from '@/modules/common/utils/preventFormDefault.ts';
import { useCurrentUserRegistersQuery } from '@/modules/investor-dashboard/queries/registers.tsx';
import { useCurrentEntityShareholdersQuery } from '@/modules/investor-dashboard/queries/transactions.tsx';
import { DividendEventsSelectPaginate } from '@/modules/issuer-dashboard/components/DividendEventsSelectPaginate.tsx';
import HoldersSelect from '@/modules/issuer-dashboard/components/HoldersSelect.tsx';
import { SelectPortfolio } from '@/modules/issuer-dashboard/components/portfolios/SelectPortfolio.tsx';
import { getTaxReportYearOptions } from '@/modules/issuer-dashboard/helpers/taxReportYears.ts';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import {
  Alert,
  Button,
  CalendarInput,
  CalendarInputProps,
  ModalContentWrapper,
  Select,
  SelectOption,
  SelectPaginateOption,
  TextInputField,
} from 'ui';
import { AlertIcon } from 'ui/icons';
import { fixNameEnum, openInNewTab } from 'ui/utils';
import {
  BulkGenerateReportRequest,
  BulkReportType,
  GenerateBulkReportsResponse,
  GenerateReportRequest,
  GenerateReportResponse,
  GetEntityShareholdersResponse,
  GetReportListResponse,
  GetUserRegistersResponse,
  HolderSelectionMode,
  PortfolioModel,
  ReportFormat,
  ReportStatus,
  ReportType,
} from 'api';

dayjs.extend(timezone);

export const investorReportTypesOptions: SelectOption<ReportType, string>[] = [
  { value: ReportType.HoldingStatement, label: 'Holding Statement' },
  { value: ReportType.Transaction, label: 'Transaction' },
  { value: ReportType.Valuation, label: 'Valuation' },
];

export const issuerReportTypesOptions: SelectOption<ReportType, string>[] = [
  { value: ReportType.RegisterList, label: 'Register List' },
  { value: ReportType.RegisterValuation, label: 'Register Valuation' },
  { value: ReportType.CapitalMovement, label: 'Capital Movement' },
  { value: ReportType.Capital, label: 'Capital' },
  { value: ReportType.SimpleRegisterList, label: 'Simplified Register List' },
  { value: ReportType.TopHolders, label: 'Top Holders' },
  { value: ReportType.CommunicationsPreferences, label: 'Communications preferences' },
  { value: ReportType.TaxReturn, label: 'Tax Report' },
  { value: ReportType.SelfCustodyAssetReport, label: 'Custody Asset Report' },
];

export const useGetReportTypesOptions = (
  userRole?: Role | null,
  registerId?: number,
): SelectOption<ReportType, string>[] => {
  const getRegisterTypeString = useRegisterTypeStrings();

  const res: SelectOption<ReportType, string>[] = [...investorReportTypesOptions];

  if ([Role.SR_ISSUER, Role.SR_ISSUER_ADMIN].includes(userRole as Role)) {
    res.unshift(
      ...[
        ...issuerReportTypesOptions,
        {
          value: ReportType.Dividend,
          label: `${getRegisterTypeString('dividend', { capitalize: true, registerId })}`,
        },
        {
          value: ReportType.DividendPayments,
          label: `${getRegisterTypeString('dividend', {
            capitalize: true,
            registerId,
          })} Payments`,
        },
      ],
    );
  }

  res.push({
    value: ReportType.InvestorDistribution,
    label: `Investor ${getRegisterTypeString('dividend', { registerId })}`,
  });
  res.push({
    value: ReportType.DividendStatement,
    label: `${getRegisterTypeString('dividend', { capitalize: true, registerId })} statement`,
  });
  return res
    .map(item => ({ ...item, label: fixNameEnum(item.label) }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

const ONLY_PDF_REPORT = [
  // ReportType.Dividend,
  ReportType.DividendStatement,
  ReportType.DividendPayments,
  ReportType.InvestorDistribution,
];

const HOLDER_INCLUDED_REPORT = [
  ReportType.HoldingStatement,
  ReportType.Transaction,
  ReportType.DividendStatement,
  ReportType.InvestorDistribution,
  ReportType.Valuation,
  ReportType.TaxReturn,
];

const HOLDER_INCLUDED_REPORT_NOT_REQUIRED = [ReportType.Transaction];

const BULK_REPORT_TYPES = new Set<string>(Object.values(BulkReportType));

const isBulkCompatibleReportType = (type: ReportType) => BULK_REPORT_TYPES.has(type);

const REPORT_POLL_INTERVAL_MS = 5000;
const REPORT_POLL_TIMEOUT_MS = 10 * 60 * 1000;
const DROPDOWN_CLOSE_GRACE_MS = 150;

const DIVIDEND_INCLUDED_REPORT = [
  ReportType.Dividend,
  // ReportType.DividendStatement,
  ReportType.DividendPayments,
  ReportType.InvestorDistribution,
];

const NOT_SHOW_CLASS_IN_REPORT_TYPES = [
  ReportType.Dividend,
  ReportType.DividendPayments,
  // ReportType.DividendStatement,
  ReportType.InvestorDistribution,
  ReportType.Valuation,
  ReportType.TaxReturn,
  ReportType.SelfCustodyAssetReport,
];

const SHOW_ONE_END_DATE_REPORT_TYPES = [
  ReportType.RegisterList,
  ReportType.RegisterValuation,
  ReportType.SimpleRegisterList,
  ReportType.TopHolders,
  ReportType.Valuation,
  ReportType.SelfCustodyAssetReport,
];

const NO_DATE_REPORT_TYPES: ReportType[] = [
  ReportType.Dividend,
  ReportType.DividendPayments,
  ReportType.InvestorDistribution,
];

interface ExtendedGenerateReportRequest
  extends Omit<GenerateReportRequest, 'portfolio_id' | 'financial_year'> {
  portfolio_id?: number | null;
  financial_year?: number | null;
  entity_ids?: number[];
  holder_selection_mode?: HolderSelectionMode;
}

const validationSchema = yup.object<ExtendedGenerateReportRequest>({
  filename: yup
    .string()
    .trim()
    .required('Report name is required')
    .min(1, 'Report name is too short'),
  type: yup.string().trim().required('Report type is required'),
  format: yup.string().trim().required('Report format is required'),
  portfolio_id: yup.number().nullable(),
  start_date: yup
    .string()
    .trim()
    .nullable()
    .when('type', ([type], schema) => {
      if (type === ReportType.TaxReturn) {
        return schema.nullable();
      }
      if (
        !SHOW_ONE_END_DATE_REPORT_TYPES.includes(type) &&
        !DIVIDEND_INCLUDED_REPORT.includes(type) &&
        !NO_DATE_REPORT_TYPES.includes(type)
      ) {
        return schema.required('Timeframe is required');
      }
      return schema;
    }),
  end_date: yup
    .string()
    .trim()
    .when('type', ([type], schema) => {
      if (type === ReportType.TaxReturn) {
        return schema.nullable();
      }
      if (
        SHOW_ONE_END_DATE_REPORT_TYPES.includes(type) &&
        type !== ReportType.SelfCustodyAssetReport
      ) {
        return schema.required('Final date is required');
      }
      if (
        !DIVIDEND_INCLUDED_REPORT.includes(type) &&
        !NO_DATE_REPORT_TYPES.includes(type) &&
        !SHOW_ONE_END_DATE_REPORT_TYPES.includes(type)
      ) {
        return schema.required('End date is required');
      }
      return schema.nullable();
    }),
  financial_year: yup.number().when(['type'], ([type], schema) => {
    return type === ReportType.TaxReturn
      ? schema.required('Financial year is required')
      : schema.nullable();
  }),
  register_class_ids: yup.array().when('type', ([type], schema) => {
    if (type === ReportType.DividendStatement) {
      return schema.min(1, 'Class is required').required('Class is required');
    }
    if (!NOT_SHOW_CLASS_IN_REPORT_TYPES.includes(type)) {
      return schema.min(1, 'At least one class is required').required('Class is required');
    }
    return schema.nullable();
  }),
  register_id: yup.number().required('Register is required'),
  entity_id: yup
    .number()
    .when(
      ['type', 'entity_ids', 'holder_selection_mode'],
      ([type, entityIds, selectionMode], schema) => {
        return HOLDER_INCLUDED_REPORT.includes(type) &&
          selectionMode !== HolderSelectionMode.AllHolders &&
          selectionMode !== HolderSelectionMode.NonZeroHolders &&
          !(entityIds as number[] | undefined)?.length &&
          !HOLDER_INCLUDED_REPORT_NOT_REQUIRED.includes(type)
          ? schema.required('Holder is required for this report type')
          : schema.nullable();
      },
    ),
  entity_ids: yup.array().of(yup.number().required()),
  holder_selection_mode: yup.mixed<HolderSelectionMode>().oneOf(Object.values(HolderSelectionMode)),
  dividend_event_id: yup.number().when(['type'], ([type], schema) => {
    return DIVIDEND_INCLUDED_REPORT.includes(type)
      ? schema.required('This field is required for this report type')
      : schema.nullable();
  }),
});

export const reportFormatsSelection: SelectOption<ReportFormat>[] = [
  { value: ReportFormat.Pdf, label: 'PDF' },
  { value: ReportFormat.Xlsx, label: 'XLSX' },
];

export interface CreateReportModalProps {
  open: boolean;
  onClose: () => void;
  entityId?: number;
  reportType?: ReportType;
  registerClassIds?: number[];
  defaultNameReport?: string;
  autoDownloadAfterGenerated?: boolean;
  start_date?: string;
  end_date?: string;
}

export const CreateReportModal = ({
  open,
  entityId,
  reportType = ReportType.Transaction,
  registerClassIds,
  defaultNameReport,
  onClose,
  autoDownloadAfterGenerated = true,
  start_date,
  end_date,
}: CreateReportModalProps) => {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const register_id = Number(params.get(REGISTER_ID_SEARCH_PARAM_KEY));
  const [selectedDividendEvent, setSelectedDividendEvent] =
    useState<SelectPaginateOption<number> | null>(null);
  const [createdReport, setCreatedReport] = useState<GenerateReportResponse>();
  const [hasPendingBulkReport, setHasPendingBulkReport] = useState(() => !!getPendingBulkReport());
  const reportPollStartedAtRef = useRef<number>();
  const [bulkHolderSelection, setBulkHolderSelection] = useState({
    entity_ids: entityId ? [entityId] : [],
    holder_selection_mode: HolderSelectionMode.SelectedHolders,
  });
  const [holdersFilterSnapshot, setHoldersFilterSnapshot] = useState<{
    dividendEventId?: number;
    optionValues: number[];
  } | null>(null);
  const isHoldersMenuOpenRef = useRef(false);
  const ignoreNextModalCloseRef = useRef(false);
  const ignoreNextModalCloseTimerRef = useRef<number>();
  const isExplicitModalCloseRef = useRef(false);
  const taxReportYearOptions = useMemo(() => getTaxReportYearOptions(11), []);
  const resetDistributionSelection = useCallback(() => {
    setSelectedDividendEvent(null);
    setHoldersFilterSnapshot(null);
  }, []);

  const userRole = rolesManipulator.current_user_role;
  const formik = useFormik<ExtendedGenerateReportRequest>({
    initialValues: {
      register_id,
      type: reportType,
      format: ReportFormat.Pdf,
      end_date: end_date || '',
      start_date: start_date || null,
      financial_year: reportType === ReportType.TaxReturn ? taxReportYearOptions[0]?.value : null,
      filename: defaultNameReport || '',
      register_class_ids: registerClassIds || [],
      entity_id: entityId,
      entity_ids: entityId ? [entityId] : [],
      holder_selection_mode: HolderSelectionMode.SelectedHolders,
      limit: 100,
      dividend_event_id: null,
      portfolio_id: null,
    } as unknown as ExtendedGenerateReportRequest,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async values => {
      const { entity_ids, holder_selection_mode, ...singleReportValues } = values;
      const selectedEntityIds = entity_ids || [];
      const holderSelectionMode = holder_selection_mode || HolderSelectionMode.SelectedHolders;
      const isBulkGeneration =
        holderSelectionMode !== HolderSelectionMode.SelectedHolders || selectedEntityIds.length > 1;

      if (isBulkGeneration) {
        if (!isBulkCompatibleReportType(values.type)) {
          toast.error('This report type does not support bulk generation');
          return;
        }
        if (getPendingBulkReport()) {
          toast.error('A bulk report is already being generated');
          return;
        }

        await generateBulkReport({
          type: values.type as unknown as BulkReportType,
          register_id: values.register_id,
          register_class_ids: values.register_class_ids || undefined,
          format: values.format,
          filename: values.filename.trim().replace(/\.zip$/i, ''),
          start_date: values.start_date || undefined,
          end_date: values.end_date || undefined,
          dividend_event_id: values.dividend_event_id || undefined,
          financial_year: values.financial_year || undefined,
          holder_selection_mode: holderSelectionMode,
          entity_ids:
            holderSelectionMode === HolderSelectionMode.SelectedHolders
              ? selectedEntityIds
              : undefined,
        } as BulkGenerateReportRequest);
        return;
      }

      await generateReport({
        ...singleReportValues,
        entity_id: selectedEntityIds[0] || values.entity_id,
        end_date: values.type === ReportType.TaxReturn ? null : values.end_date,
        start_date: values.type === ReportType.TaxReturn ? null : values.start_date,
        register_class_ids:
          NOT_SHOW_CLASS_IN_REPORT_TYPES.includes(values.type) &&
          values.type !== ReportType.DividendStatement
            ? null
            : values.register_class_ids,
      } as GenerateReportRequest);
    },
  });

  const handleOnSuccess = useCallback(
    (data: GenerateReportResponse) => {
      const nextParams = new URLSearchParams(params);
      nextParams.set(REGISTER_ID_SEARCH_PARAM_KEY, String(data.report.register_id));
      setParams(nextParams, { replace: false });

      formik.resetForm();
      resetDistributionSelection();
      setBulkHolderSelection({
        entity_ids: entityId ? [entityId] : [],
        holder_selection_mode: HolderSelectionMode.SelectedHolders,
      });

      if (
        (userRole === Role.SR_ISSUER || userRole === Role.SR_ISSUER_ADMIN) &&
        !matchPath(issuerDashboardPaths.static.reports, location.pathname)
      ) {
        navigate(
          { pathname: issuerDashboardPaths.static.reports, search: nextParams.toString() },
          {
            replace: false,
          },
        );
      }

      switch (data.report.status) {
        case ReportStatus.Completed:
          if (data.report.file_storage_item_url && autoDownloadAfterGenerated) {
            openInNewTab(data.report.file_storage_item_url);
          }
          onClose();
          break;

        case ReportStatus.Generating:
          if (autoDownloadAfterGenerated) {
            reportPollStartedAtRef.current = Date.now();
            setCreatedReport(data);
            return;
          }
          onClose();
          break;

        default:
          onClose();
          break;
      }
    },
    [
      autoDownloadAfterGenerated,
      entityId,
      location.pathname,
      navigate,
      onClose,
      params,
      resetDistributionSelection,
      setParams,
      userRole,
    ],
  );

  const { mutateAsync: generateReport, isLoading: isReportGeneration } = useCreateReportMutation({
    setErrors: formik.setErrors,
    onSuccess: handleOnSuccess,
  });

  const handleBulkSuccess = (data: GenerateBulkReportsResponse) => {
    const nextParams = new URLSearchParams(params);
    nextParams.set(REGISTER_ID_SEARCH_PARAM_KEY, String(data.report.register_id));
    setParams(nextParams, { replace: false });

    formik.resetForm();
    resetDistributionSelection();
    setBulkHolderSelection({
      entity_ids: entityId ? [entityId] : [],
      holder_selection_mode: HolderSelectionMode.SelectedHolders,
    });

    if (!matchPath(issuerDashboardPaths.static.reports, location.pathname)) {
      navigate(
        { pathname: issuerDashboardPaths.static.reports, search: nextParams.toString() },
        {
          replace: false,
        },
      );
    }

    if (autoDownloadAfterGenerated) {
      setPendingBulkReport({
        id: data.report.id,
        registerId: data.report.register_id,
        startedAt: Date.now(),
      });
    }

    onClose();
  };

  const { mutateAsync: generateBulkReport, isLoading: isBulkReportGeneration } =
    useCreateBulkReportMutation({
      setErrors: formik.setErrors,
      onSuccess: handleBulkSuccess,
    });

  const handleOnCompleted = useCallback(
    (reportList?: GetReportListResponse): number | false => {
      if (!createdReport?.report) return false;

      const report = reportList?.data?.find(item => item.id === createdReport?.report?.id);

      if (
        reportPollStartedAtRef.current &&
        Date.now() - reportPollStartedAtRef.current >= REPORT_POLL_TIMEOUT_MS
      ) {
        toast.error(
          'Report generation is taking longer than expected. Check the Reports page later.',
        );
        onClose();
        setCreatedReport(undefined);
        reportPollStartedAtRef.current = undefined;
        return false;
      }

      if (!report || report.status === ReportStatus.Generating) return REPORT_POLL_INTERVAL_MS;

      if (report.status === ReportStatus.Completed && report.file_storage_item_url) {
        openInNewTab(report.file_storage_item_url);
      } else if (report.status === ReportStatus.Completed) {
        toast.error('Report was generated without a download URL');
      }

      if (report.status === ReportStatus.Failed) {
        toast.error('Report generation failed');
      }

      onClose();
      setCreatedReport(undefined);
      reportPollStartedAtRef.current = undefined;

      return false;
    },
    [createdReport?.report, onClose],
  );

  useGetReportList({
    payload: {
      page: 1,
      page_size: 15,
      type: createdReport?.report?.type,
      entity_id: createdReport?.report.entity_id,
      register_id: createdReport?.report?.register_id,
      filename: createdReport?.report?.filename,
      format: createdReport?.report?.format,
    },
    disabled: !createdReport?.report,
    onCompleted: handleOnCompleted,
  });

  const investorRegisters = useCurrentEntityShareholdersQuery(
    Number(params.get(ENTITY_ID_SEARCH_PARAM_KEY)),
    [Role.SR_ISSUER, Role.SR_ISSUER_ADMIN].includes(userRole as Role),
  );

  const reportTypesOptions = useGetReportTypesOptions(
    userRole,
    formik.values.register_id && !isNaN(+formik.values.register_id)
      ? +formik.values.register_id
      : undefined,
  );
  const selectableReportTypesOptions = reportTypesOptions;

  const issuerRegisters = useCurrentUserRegistersQuery(userRole === Role.SR_INVESTOR);
  const isInvestorDistributionReport = formik.values.type === ReportType.InvestorDistribution;
  const selectedDividendEventIdForHolderFilter =
    isInvestorDistributionReport && formik.values.dividend_event_id
      ? formik.values.dividend_event_id
      : undefined;
  const selectedHolderEntityIds = useMemo(() => {
    if (!isInvestorDistributionReport) return [];

    if (
      formik.values.holder_selection_mode &&
      formik.values.holder_selection_mode !== HolderSelectionMode.SelectedHolders
    ) {
      return [];
    }

    if ((formik.values.entity_ids || []).length) {
      return (formik.values.entity_ids || []).filter(
        (entityId): entityId is number => typeof entityId === 'number' && !Number.isNaN(entityId),
      );
    }

    return typeof formik.values.entity_id === 'number' && !Number.isNaN(formik.values.entity_id)
      ? [formik.values.entity_id]
      : [];
  }, [
    formik.values.entity_id,
    formik.values.entity_ids,
    formik.values.holder_selection_mode,
    isInvestorDistributionReport,
  ]);
  const selectedHolderEntityIdForEventFilter =
    selectedHolderEntityIds.length === 1 ? selectedHolderEntityIds[0] : undefined;
  const hasEligibleInvestorDistributionHolders =
    !!selectedDividendEventIdForHolderFilter &&
    holdersFilterSnapshot?.dividendEventId === selectedDividendEventIdForHolderFilter &&
    holdersFilterSnapshot.optionValues.length > 0;
  const showNoInvestorDistributionHoldersMessage =
    !!selectedDividendEventIdForHolderFilter &&
    holdersFilterSnapshot?.dividendEventId === selectedDividendEventIdForHolderFilter &&
    holdersFilterSnapshot.optionValues.length === 0;
  const handleHoldersFilterOptionsLoaded = useCallback(
    ({ dividendEventId, optionValues }: { dividendEventId?: number; optionValues: number[] }) => {
      setHoldersFilterSnapshot(prev => {
        const isSameValues =
          prev?.optionValues.length === optionValues.length &&
          prev.optionValues.every((value, index) => value === optionValues[index]);

        if (prev?.dividendEventId === dividendEventId && isSameValues) {
          return prev;
        }

        return { dividendEventId, optionValues };
      });
    },
    [],
  );

  const { data: registersResponse, isLoading: isRegistersLoading } = (
    userRole === Role.SR_INVESTOR ? investorRegisters : issuerRegisters
  ) as UseQueryResult<GetEntityShareholdersResponse & GetUserRegistersResponse, unknown>;

  const registers = useMemo(() => {
    const data = registersResponse?.data || registersResponse?.shareholders;
    if (!data) return [];

    return data?.map(item => ({
      label: item.register_obj?.name ?? '',
      value: item.register_id,
    }));
  }, [registersResponse]);

  useEffect(() => {
    if (!isInvestorDistributionReport || !selectedDividendEventIdForHolderFilter) {
      return;
    }

    if (
      !holdersFilterSnapshot ||
      holdersFilterSnapshot.dividendEventId !== selectedDividendEventIdForHolderFilter
    ) {
      return;
    }

    if (!selectedHolderEntityIds.length) {
      return;
    }

    const nextEntityIds = selectedHolderEntityIds.filter(entityId =>
      holdersFilterSnapshot.optionValues.includes(entityId),
    );

    if (nextEntityIds.length === selectedHolderEntityIds.length) {
      return;
    }

    formik.setFieldValue('entity_ids', nextEntityIds);
    formik.setFieldValue('entity_id', nextEntityIds.length === 1 ? nextEntityIds[0] : undefined);
    formik.setFieldError('entity_id', '');
    formik.setFieldError('entity_ids', '');
  }, [
    formik,
    holdersFilterSnapshot,
    isInvestorDistributionReport,
    selectedDividendEventIdForHolderFilter,
    selectedHolderEntityIds,
  ]);

  useEffect(() => {
    if (!isInvestorDistributionReport) {
      return;
    }

    if (formik.values.holder_selection_mode !== HolderSelectionMode.AllHolders) {
      return;
    }

    if (!showNoInvestorDistributionHoldersMessage) {
      return;
    }

    formik.setFieldValue('holder_selection_mode', HolderSelectionMode.SelectedHolders);
  }, [
    formik,
    isInvestorDistributionReport,
    formik.values.holder_selection_mode,
    showNoInvestorDistributionHoldersMessage,
  ]);

  const handleRegisterChange = useCallback(
    (value: number | null) => {
      if (!value) return;
      resetDistributionSelection();
      formik.setFieldValue('dividend_event_id', null);
      formik.setFieldValue('register_id', value);
      formik.setFieldValue('entity_id', undefined);
      formik.setFieldValue('entity_ids', []);
      formik.setFieldValue('holder_selection_mode', HolderSelectionMode.SelectedHolders);
      formik.setFieldValue(
        'register_class_ids',
        NOT_SHOW_CLASS_IN_REPORT_TYPES.includes(formik.values.type) &&
          formik.values.type !== ReportType.DividendStatement
          ? null
          : [],
      );
      setBulkHolderSelection({
        entity_ids: entityId ? [entityId] : [],
        holder_selection_mode: HolderSelectionMode.SelectedHolders,
      });
      formik.setFieldError('register_id', '');
      const currentRegister = params.get(REGISTER_ID_SEARCH_PARAM_KEY);

      if (currentRegister === String(value)) return;

      const nextParams = new URLSearchParams(params);
      nextParams.set(REGISTER_ID_SEARCH_PARAM_KEY, value.toString());
      setParams(nextParams, { replace: false });
      const registerName = registers?.find(item => item.value === value)?.label;
      toast.success(`We have changed your current register to ${registerName}`, {
        duration: 4000,
      });
    },
    [entityId, formik.values.type, params, registers, resetDistributionSelection, setParams],
  );

  const handleChangePortfolio = useCallback(
    (option: SelectPaginateOption<PortfolioModel> | null) => {
      formik.setFieldValue('portfolio_id', option?.value?.id ?? null);
    },
    [formik],
  );

  const handleReportTypeChange = (type: ReportType | null) => {
    formik.setFieldValue('type', type);
    if (!type) return;
    const supportsHolders = HOLDER_INCLUDED_REPORT.includes(type);
    const supportsDividendEvent = DIVIDEND_INCLUDED_REPORT.includes(type);
    const cachedDividendEventId = selectedDividendEvent?.value ?? null;
    //issuer without default entityId props, investor with entityId props
    if (!entityId && !supportsHolders) {
      formik.setFieldValue('entity_id', undefined);
    }
    if (!entityId) {
      if (supportsHolders && isBulkCompatibleReportType(type)) {
        const numericEntityIds = bulkHolderSelection.entity_ids.filter(
          (selectedEntityId): selectedEntityId is number =>
            typeof selectedEntityId === 'number' && !Number.isNaN(selectedEntityId),
        );
        const isInvestorDistribution = type === ReportType.InvestorDistribution;
        const nextSelectionMode =
          isInvestorDistribution &&
          bulkHolderSelection.holder_selection_mode === HolderSelectionMode.NonZeroHolders
            ? HolderSelectionMode.SelectedHolders
            : isInvestorDistribution &&
              bulkHolderSelection.holder_selection_mode === HolderSelectionMode.AllHolders
            ? cachedDividendEventId
              ? HolderSelectionMode.AllHolders
              : HolderSelectionMode.SelectedHolders
            : bulkHolderSelection.holder_selection_mode;
        const nextEntityIds =
          nextSelectionMode === HolderSelectionMode.SelectedHolders ? numericEntityIds : [];

        formik.setFieldValue('entity_ids', nextEntityIds);
        formik.setFieldValue('holder_selection_mode', nextSelectionMode);
        formik.setFieldValue(
          'entity_id',
          nextEntityIds.length === 1 ? nextEntityIds[0] : undefined,
        );
      } else {
        formik.setFieldValue('entity_ids', []);
        formik.setFieldValue('holder_selection_mode', HolderSelectionMode.SelectedHolders);
        formik.setFieldValue(
          'entity_id',
          supportsHolders && bulkHolderSelection.entity_ids.length === 1
            ? bulkHolderSelection.entity_ids[0]
            : undefined,
        );
      }
    }
    formik.setFieldError('entity_id', '');
    formik.setFieldError('entity_ids', '');

    if (type !== ReportType.SelfCustodyAssetReport) {
      formik.setFieldValue('portfolio_id', null);
    }

    if (NOT_SHOW_CLASS_IN_REPORT_TYPES.includes(type) && type !== ReportType.DividendStatement) {
      formik.setFieldValue('register_class_ids', null);
    }
    formik.setFieldError('register_class_ids', '');

    if (ONLY_PDF_REPORT.includes(type)) {
      formik.setFieldValue('format', ReportFormat.Pdf);
    }
    formik.setFieldError('format', '');

    if (NO_DATE_REPORT_TYPES.includes(type)) {
      formik.setFieldValue('start_date', null);
      formik.setFieldValue('end_date', null);
    }
    formik.setFieldError('start_date', '');
    formik.setFieldError('end_date', '');
    if (SHOW_ONE_END_DATE_REPORT_TYPES.includes(type)) {
      formik.setFieldValue('start_date', null);
    }
    if (type === ReportType.TaxReturn) {
      formik.setFieldValue('end_date', null);
      formik.setFieldValue('start_date', null);
      formik.setFieldValue('financial_year', taxReportYearOptions[0]?.value || null);
    } else {
      formik.setFieldValue('financial_year', null);
      formik.setFieldError('financial_year', '');
    }
    formik.setFieldValue('dividend_event_id', supportsDividendEvent ? cachedDividendEventId : null);
    formik.setFieldError('dividend_event_id', '');
  };

  const handleChangeRegisterClass = (
    options: SelectPaginateOption<ClassItemOption>[] | SelectPaginateOption<ClassItemOption>,
  ) => {
    if (Array.isArray(options)) {
      const register_class_ids = options.map(v => v.value.id);
      formik.setFieldValue('register_class_ids', register_class_ids);
      formik.setFieldError('register_class_ids', '');
    } else {
      formik.setFieldValue('register_class_ids', [options.value?.id]);
      formik.setFieldError('register_class_ids', '');
    }
  };

  const handleReportFormatChange = (format: ReportFormat | null) => {
    formik.setFieldValue('format', format);
    formik.setFieldError('format', '');
  };

  const handleReportRangeDateChange: CalendarInputProps['rangeOnChange'] = dates => {
    formik.setFieldError('start_date', '');
    formik.setFieldError('end_date', '');
    if (dates) {
      const start_date = dayjs(dates[0]).format('YYYY-MM-DD');
      const end_date = dayjs(dates[1]).format('YYYY-MM-DD');

      formik.setFieldValue('start_date', start_date);
      formik.setFieldValue('end_date', end_date);
      return;
    }
    formik.setFieldValue('start_date', null);
    formik.setFieldValue('end_date', null);
  };

  const handleReportDateChange: CalendarInputProps['onChange'] = date => {
    formik.setFieldError('start_date', '');
    formik.setFieldError('end_date', '');
    formik.setFieldValue('start_date', null);
    if (date) {
      const end_date = dayjs(date).format('YYYY-MM-DD');

      formik.setFieldValue('end_date', end_date);
      return;
    }
    formik.setFieldValue('end_date', null);
  };

  const handleChangeEntityId = (entityId: number | null) => {
    const nextSelectionMode =
      entityId === null
        ? HolderSelectionMode.SelectedHolders
        : formik.values.holder_selection_mode || HolderSelectionMode.SelectedHolders;

    formik.setFieldValue('entity_id', entityId);
    formik.setFieldValue('holder_selection_mode', nextSelectionMode);
    setBulkHolderSelection({
      entity_ids: entityId === null ? [] : [entityId],
      holder_selection_mode: nextSelectionMode,
    });
    formik.setFieldError('entity_id', '');
  };

  const handleChangeEntityIds = (entityIds: number[] | null) => {
    const nextEntityIds = (entityIds || []).filter(
      (entityId): entityId is number => typeof entityId === 'number' && !Number.isNaN(entityId),
    );
    formik.setFieldValue('entity_ids', nextEntityIds);
    formik.setFieldValue('entity_id', nextEntityIds.length === 1 ? nextEntityIds[0] : undefined);
    formik.setFieldValue('holder_selection_mode', HolderSelectionMode.SelectedHolders);
    setBulkHolderSelection({
      entity_ids: nextEntityIds,
      holder_selection_mode: HolderSelectionMode.SelectedHolders,
    });
    formik.setFieldError('entity_id', '');
    formik.setFieldError('entity_ids', '');
  };

  const handleHolderSelectionModeChange = (holderSelectionMode: HolderSelectionMode) => {
    formik.setFieldValue('holder_selection_mode', holderSelectionMode);

    const nextEntityIds =
      holderSelectionMode !== HolderSelectionMode.SelectedHolders
        ? []
        : formik.values.entity_ids || [];

    if (holderSelectionMode !== HolderSelectionMode.SelectedHolders) {
      formik.setFieldValue('entity_id', undefined);
      formik.setFieldValue('entity_ids', []);
    }
    setBulkHolderSelection({
      entity_ids: nextEntityIds,
      holder_selection_mode: holderSelectionMode,
    });
    formik.setFieldError('entity_id', '');
    formik.setFieldError('entity_ids', '');
  };

  const handleChangeDividendEvent = (option: SelectPaginateOption<number> | null) => {
    setHoldersFilterSnapshot(null);
    setSelectedDividendEvent(option);
    formik.setFieldValue('dividend_event_id', option?.value);
    if (
      isInvestorDistributionReport &&
      !option &&
      formik.values.holder_selection_mode === HolderSelectionMode.AllHolders
    ) {
      formik.setFieldValue('holder_selection_mode', HolderSelectionMode.SelectedHolders);
    }
    formik.setFieldError('dividend_event_id', '');
  };

  const getAustraliaDisabledDate = useCallback((date: dayjs.Dayjs) => {
    const selectedDateAustralia = dayjs.tz(date.format('YYYY-MM-DD'), 'Australia/Sydney');
    const nowAustralia = dayjs().tz('Australia/Sydney');
    return selectedDateAustralia.isAfter(nowAustralia, 'day');
  }, []);

  const handleCloseModalWithFormReset = () => {
    if (
      !isExplicitModalCloseRef.current &&
      (isHoldersMenuOpenRef.current || ignoreNextModalCloseRef.current)
    ) {
      isHoldersMenuOpenRef.current = false;
      ignoreNextModalCloseRef.current = false;
      return;
    }

    isExplicitModalCloseRef.current = false;
    formik.resetForm();
    resetDistributionSelection();
    setBulkHolderSelection({
      entity_ids: entityId ? [entityId] : [],
      holder_selection_mode: HolderSelectionMode.SelectedHolders,
    });
    onClose();
  };

  const handleHoldersMenuOpen = () => {
    if (ignoreNextModalCloseTimerRef.current) {
      clearTimeout(ignoreNextModalCloseTimerRef.current);
    }

    isHoldersMenuOpenRef.current = true;
    ignoreNextModalCloseRef.current = true;
  };

  const handleHoldersMenuClose = () => {
    ignoreNextModalCloseRef.current = true;

    if (ignoreNextModalCloseTimerRef.current) {
      clearTimeout(ignoreNextModalCloseTimerRef.current);
    }

    // Keep the modal open when the same outside click first closes the react-select menu.
    ignoreNextModalCloseTimerRef.current = window.setTimeout(() => {
      isHoldersMenuOpenRef.current = false;
      ignoreNextModalCloseRef.current = false;
      ignoreNextModalCloseTimerRef.current = undefined;
    }, DROPDOWN_CLOSE_GRACE_MS);
  };

  const handleExplicitModalCloseStart = () => {
    isExplicitModalCloseRef.current = true;
  };

  useEffect(() => {
    return () => {
      if (ignoreNextModalCloseTimerRef.current) {
        clearTimeout(ignoreNextModalCloseTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (open) return;

    isExplicitModalCloseRef.current = false;
    isHoldersMenuOpenRef.current = false;
    ignoreNextModalCloseRef.current = false;

    if (ignoreNextModalCloseTimerRef.current) {
      clearTimeout(ignoreNextModalCloseTimerRef.current);
      ignoreNextModalCloseTimerRef.current = undefined;
    }
  }, [open]);

  useEffect(() => {
    const handlePendingBulkReportChange = () => setHasPendingBulkReport(!!getPendingBulkReport());

    window.addEventListener(PENDING_BULK_REPORT_CHANGE_EVENT, handlePendingBulkReportChange);
    return () => {
      window.removeEventListener(PENDING_BULK_REPORT_CHANGE_EVENT, handlePendingBulkReportChange);
    };
  }, []);

  const isBulkGenerationSelected =
    (formik.values.holder_selection_mode || HolderSelectionMode.SelectedHolders) !==
      HolderSelectionMode.SelectedHolders || (formik.values.entity_ids?.length || 0) > 1;
  const isBulkGenerationBlocked = isBulkGenerationSelected && hasPendingBulkReport;

  return (
    <ModalContentWrapper
      open={open}
      title="Generate new report"
      onClose={handleCloseModalWithFormReset}
      onCloseButtonMouseDown={handleExplicitModalCloseStart}
    >
      <FormikProvider value={formik}>
        <form className="w-full flex flex-col space-y-10" onSubmit={preventFormDefault}>
          <TextInputField name="filename" label="report name" placeholder="Name" type="text" />
          <Select
            label="Report type"
            options={selectableReportTypesOptions}
            value={formik.values.type}
            onChange={handleReportTypeChange}
          />
          {formik.values.type == 'top_holders' && (
            <TextInputField label="limit" name="limit" type="number" min={1} placeholder="Limit" />
          )}
          <Select
            label="Register"
            options={registers}
            value={formik.values.register_id}
            onChange={handleRegisterChange}
            isLoading={isRegistersLoading}
            error={formik.errors.register_id}
          />
          {!NOT_SHOW_CLASS_IN_REPORT_TYPES.includes(formik.values.type) &&
            formik.values.type !== ReportType.DividendStatement && (
              <RegisterClassesSelectPaginate
                isMulti={true}
                key={formik.values.register_id}
                disabled={!formik.values.register_id}
                label="CLASS"
                onChange={handleChangeRegisterClass}
                value={formik.values.register_class_ids}
                onMenuOpen={handleHoldersMenuOpen}
                onMenuClose={handleHoldersMenuClose}
                showSelectedOptionsInMenu={true}
                error={
                  typeof formik?.errors?.register_class_ids === 'object'
                    ? formik.errors.register_class_ids[0]
                    : formik.errors.register_class_ids
                }
                formFieldTitle=""
                showAddNewItem={userRole !== Role.SR_INVESTOR}
                showPriceInLabel={false}
              />
            )}
          {formik.values.type === ReportType.DividendStatement && (
            <RegisterClassesSelectPaginate
              label="Class"
              key={formik.values.register_id}
              disabled={!formik.values.register_id}
              onChange={handleChangeRegisterClass}
              value={formik.values.register_class_ids?.[0]}
              isMulti={false}
              onMenuOpen={handleHoldersMenuOpen}
              onMenuClose={handleHoldersMenuClose}
              formFieldTitle=""
              error={
                typeof formik?.errors?.register_class_ids === 'object'
                  ? formik.errors.register_class_ids[0]
                  : formik.errors.register_class_ids
              }
              showAddNewItem={userRole !== Role.SR_INVESTOR}
            />
          )}
          {DIVIDEND_INCLUDED_REPORT.includes(formik.values.type) && (
            <DividendEventsSelectPaginate
              onChange={handleChangeDividendEvent}
              error={formik.errors.dividend_event_id}
              value={selectedDividendEvent}
              entityId={selectedHolderEntityIdForEventFilter}
              disableWhileLoading={isInvestorDistributionReport}
            />
          )}
          {formik.values.type === ReportType.SelfCustodyAssetReport && (
            <SelectPortfolio
              label="Portfolio (optional)"
              value={formik.values.portfolio_id ?? undefined}
              onChange={handleChangePortfolio}
              clearable={true}
              placeholder="All portfolios"
            />
          )}
          {/*issuer without default entityId props, investor with entityId props*/}
          {!entityId &&
            HOLDER_INCLUDED_REPORT.includes(formik.values.type) &&
            isBulkCompatibleReportType(formik.values.type) &&
            [Role.SR_ISSUER, Role.SR_ISSUER_ADMIN].includes(userRole as Role) && (
              <>
                <HoldersSelect
                  label={`Holders${
                    HOLDER_INCLUDED_REPORT_NOT_REQUIRED.includes(formik.values.type)
                      ? ' (optional)'
                      : ''
                  }`}
                  useEntityId
                  isMulti={true}
                  showSelectionModeShortcuts={true}
                  selectionMode={formik.values.holder_selection_mode}
                  disabled={!formik.values.register_id}
                  error={
                    typeof formik.errors.entity_ids === 'string'
                      ? formik.errors.entity_ids
                      : formik.errors.entity_id
                  }
                  value={formik.values.entity_ids || []}
                  onChange={handleChangeEntityIds}
                  onSelectionModeChange={handleHolderSelectionModeChange}
                  onMenuOpen={handleHoldersMenuOpen}
                  onMenuClose={handleHoldersMenuClose}
                  dividendEventId={selectedDividendEventIdForHolderFilter}
                  disableWhileLoading={isInvestorDistributionReport}
                  onOptionsLoaded={handleHoldersFilterOptionsLoaded}
                  selectionModeShortcuts={
                    isInvestorDistributionReport
                      ? hasEligibleInvestorDistributionHolders
                        ? [HolderSelectionMode.AllHolders]
                        : []
                      : [HolderSelectionMode.AllHolders, HolderSelectionMode.NonZeroHolders]
                  }
                />
                {isInvestorDistributionReport && showNoInvestorDistributionHoldersMessage && (
                  <div className="mt-2 text-sm text-[#DC2626]">
                    No holders are available for the selected event.
                  </div>
                )}
              </>
            )}
          {!entityId &&
            HOLDER_INCLUDED_REPORT.includes(formik.values.type) &&
            ![Role.SR_ISSUER, Role.SR_ISSUER_ADMIN].includes(userRole as Role) && (
              <HoldersSelect
                label={`Holder${
                  HOLDER_INCLUDED_REPORT_NOT_REQUIRED.includes(formik.values.type)
                    ? ' (optional)'
                    : ''
                }`}
                useEntityId
                isMulti={false}
                disabled={!formik.values.register_id}
                error={formik.errors.entity_id}
                value={formik.values.entity_id || null}
                onChange={handleChangeEntityId}
                onMenuOpen={handleHoldersMenuOpen}
                onMenuClose={handleHoldersMenuClose}
                dividendEventId={selectedDividendEventIdForHolderFilter}
                disableWhileLoading={isInvestorDistributionReport}
                onOptionsLoaded={handleHoldersFilterOptionsLoaded}
              />
            )}
          {!NO_DATE_REPORT_TYPES.includes(formik.values.type) && (
            <>
              {formik.values.type === ReportType.TaxReturn ? (
                <Select<number>
                  label="Financial year"
                  options={taxReportYearOptions}
                  value={formik.values.financial_year || undefined}
                  onChange={value => {
                    formik.setFieldValue('financial_year', value || null);
                    formik.setFieldError('financial_year', '');
                  }}
                  error={formik.errors.financial_year}
                />
              ) : (
                <CalendarInput
                  name="time_frame"
                  disabledDate={getAustraliaDisabledDate}
                  label={
                    SHOW_ONE_END_DATE_REPORT_TYPES.includes(formik.values.type)
                      ? 'FINAL DATE'
                      : 'TIMEFRAME'
                  }
                  error={formik.errors.start_date || formik.errors.end_date}
                  rangeOnChange={handleReportRangeDateChange}
                  onChange={handleReportDateChange}
                  value={
                    SHOW_ONE_END_DATE_REPORT_TYPES.includes(formik.values.type) &&
                    formik.values.end_date
                      ? dayjs(formik.values.end_date)
                      : null
                  }
                  rangeValue={
                    !SHOW_ONE_END_DATE_REPORT_TYPES.includes(formik.values.type) &&
                    formik.values.end_date &&
                    formik.values.start_date
                      ? [dayjs(formik.values.start_date), dayjs(formik.values.end_date)]
                      : null
                  }
                  range={!SHOW_ONE_END_DATE_REPORT_TYPES.includes(formik.values.type)}
                />
              )}
            </>
          )}
          <Select
            label="Report format"
            options={reportFormatsSelection.filter(item =>
              ONLY_PDF_REPORT.includes(formik.values.type)
                ? item.value !== ReportFormat.Xlsx
                : true,
            )}
            defaultValue={ReportFormat.Pdf}
            value={formik.values.format}
            onChange={handleReportFormatChange}
          />
          {isBulkGenerationBlocked && (
            <Alert icon={AlertIcon} variant="error" className="mb-0">
              <div>
                Another bulk report is currently being generated. Please wait for it to finish.
              </div>
            </Alert>
          )}
          <Button
            type="submit"
            loading={isReportGeneration || isBulkReportGeneration || !!createdReport}
            onClick={formik.handleSubmit}
            disabled={
              isRegistersLoading ||
              isReportGeneration ||
              isBulkReportGeneration ||
              isBulkGenerationBlocked ||
              !!createdReport
            }
          >
            Generate
          </Button>
        </form>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
